import React, { useState, useRef } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FiCamera, FiPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import api from "../api";
import TagAutocomplete from "./TagAutocomplete";
import "./style/SocialGallery.css";

const MAX_FILES = 10;

/**
 * Upload modal: multi-file picker + caption + optional @tag.
 *
 * Props:
 *   isOpen     bool
 *   onClose    () => void
 *   eventId    number
 *   onUploaded (photo) => void
 */
export default function SocialUploadModal({ isOpen, onClose, eventId, onUploaded }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [caption, setCaption] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef();

  function handleFiles(fileList) {
    const incoming = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (incoming.length === 0) {
      setError(t("social_upload.error_images_only"));
      return;
    }
    const combined = [...files, ...incoming].slice(0, MAX_FILES);
    if (files.length + incoming.length > MAX_FILES) {
      setError(t("social_upload.error_max_exceeded", { max: MAX_FILES }));
    } else {
      setError(null);
    }
    setFiles(combined);

    // Generate previews for newly added files
    const startIdx = files.length;
    incoming.slice(0, MAX_FILES - files.length).forEach((f, i) => {
      const idx = startIdx + i;
      const reader = new FileReader();
      reader.onload = (e) =>
        setPreviews((prev) => {
          const next = [...prev];
          next[idx] = e.target.result;
          return next;
        });
      reader.readAsDataURL(f);
    });
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }

  async function handleSubmit() {
    if (files.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append("eid", eventId);
    if (caption.trim()) formData.append("caption", caption.trim());
    files.forEach((f) => formData.append("files", f));

    try {
      const res = await api.post("/social/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        onUploaded(res.data.photo);
        handleClose();
      } else {
        setError(t("social_upload.error_upload_failed"));
      }
    } catch (e) {
      setError(e.response?.data?.detail || t("social_upload.error_upload_failed"));
    }
    setSubmitting(false);
  }

  function handleClose() {
    setFiles([]);
    setPreviews([]);
    setCaption("");
    setTagInput("");
    setError(null);
    onClose();
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>{t("social_upload.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {files.length === 0 ? (
          /* Drop zone — shown when no files selected */
          <div
            className={`social-upload-dropzone ${dragActive ? "social-upload-dropzone--active" : ""}`}
            onClick={() => inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            <FiCamera size={36} style={{ color: "#8e8e8e", marginBottom: 8 }} />
            <p style={{ color: "#8e8e8e", margin: 0, fontSize: 14 }}>
              {t("social_upload.drop_hint")}
            </p>
            <p style={{ color: "#b0b3b8", margin: "4px 0 0", fontSize: 12 }}>
              {t("social_upload.max_hint", { max: MAX_FILES })}
            </p>
          </div>
        ) : (
          /* Preview grid — shown when files are selected */
          <div className="social-upload-preview-grid">
            {previews.map((src, i) => (
              <div key={i} className="social-upload-preview-item">
                {src && <img src={src} alt={`preview-${i}`} />}
                <button
                  className="social-upload-preview-remove"
                  onClick={() => removeFile(i)}
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
            {files.length < MAX_FILES && (
              <button
                className="social-upload-preview-add"
                onClick={() => inputRef.current.click()}
                aria-label="Add more photos"
              >
                <FiPlus size={22} />
              </button>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />

        {files.length > 0 && (
          <p style={{ fontSize: 12, color: "#8e8e8e", margin: "8px 0 0" }}>
            {t("social_upload.photos_selected", { count: files.length, max: MAX_FILES })}
          </p>
        )}

        {/* Caption */}
        <div className="mt-3">
          <textarea
            className="form-control form-control-sm"
            placeholder="Write a caption…"
            rows={2}
            maxLength={500}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            style={{ resize: "none", fontSize: 13 }}
          />
        </div>

        {/* Tag input */}
        <div className="mt-2">
          <TagAutocomplete
            eventId={eventId}
            value={tagInput}
            onChange={setTagInput}
            onSelect={() => {}}
            placeholder="Tag someone (@name)"
          />
        </div>

        {error && <p className="text-danger mt-2" style={{ fontSize: 13 }}>{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={files.length === 0 || submitting}
        >
          {submitting ? <Spinner animation="border" size="sm" /> : "Share"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
